import React, { useState } from 'react'
import { gql, useQuery, useMutation } from '@apollo/client'
import { useSnackbar } from 'notistack'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import EventIcon from 'components/material-symbol/icons/Event'
import CheckIcon from 'components/material-symbol/icons/Check'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import {
  DesktopTimePicker,
  DesktopDatePicker,
  LocalizationProvider,
} from '@mui/x-date-pickers'
import {
  enterTimeViewRenderers,
  mergeEnterTimePickerSlotProps,
  ENTER_TIME_FIELD_FORMAT,
} from 'components/pga-time-picker/enter-time-view'
import {
  mergeDesktopDatePickerSlotProps,
  mergeDesktopDatePickerSlots,
} from 'components/pga-time-picker/desktop-date-picker'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import themes from 'themes'
import CustomLuxonAdapter from 'lib/CustomLuxonAdapter'
import { toThreeLetterTimezone } from 'utils/timezones'
import SubviewPageHeader from 'pages/pga-coach/bookings/events/event-details-dialog/subview-page-header'
import { outlinedPickerFieldSx } from 'lib/form-field-styles'
import { useStyles } from './styles'

const LESSON_VALIDATION_QUERY = gql`
  query lessonValidation($lessonId: ID!, $timespans: [DateTimeRangeInput!]!) {
    lessonValidation(lessonId: $lessonId, timespans: $timespans) {
      isAvailable
      warnings
    }
  }
`

const UPDATE_SESSION = gql`
  mutation UpdateSession($id: ID!, $startDateTime: ISO8601DateTime!) {
    updateSession(id: $id, startDateTime: $startDateTime) {
      message
      success
      session {
        id
        startDateTime
      }
    }
  }
`

const PICKER_ICON_COLOR = '#172024'

const dialogPickerPopperProps = {
  disablePortal: false,
  placement: 'bottom-start',
}

const DatePickerIcon = props => (
  <EventIcon fill={0} {...props} htmlColor={PICKER_ICON_COLOR} />
)

const TimePickerIcon = props => (
  <ScheduleIcon fill={0} {...props} htmlColor={PICKER_ICON_COLOR} />
)

const EditSessionSubview = ({ booking, onSuccess, onBack }) => {
  const { startDateTime, timezone } = booking
  const formattedTimezone = toThreeLetterTimezone(timezone)
  const classes = useStyles()
  const currentDateTime = DateTime.local()
  const [startDate, setStartDate] = useState(
    DateTime.fromISO(startDateTime).setZone(timezone),
  )
  const [startTime, setStartTime] = useState(
    DateTime.fromISO(startDateTime).setZone(timezone),
  )
  const [warnings, setWarnings] = useState([])
  const [inputError, setInputError] = useState(null)
  const [isSaving, setIsSaving] = useState(false)

  const { refetch } = useQuery(LESSON_VALIDATION_QUERY, {
    skip: true,
    notifyOnNetworkStatusChange: true,
  })

  const { enqueueSnackbar } = useSnackbar()
  const [updateSession] = useMutation(UPDATE_SESSION)
  const coachTheme = createTheme(themes.pgaCoach)

  const buildSessionStartDateTime = () =>
    startDate.set({
      hour: startTime.hour,
      minute: startTime.minute,
      second: 0,
      millisecond: 0,
    })

  const handleTimeChange = val => {
    if (!val || val.invalid) return
    setStartTime(val)
    validateSession({ time: val })
  }

  const handleDateChange = val => {
    if (!val || val.invalid) return
    setStartDate(val)
    setStartTime(prev =>
      prev.set({ year: val.year, month: val.month, day: val.day }),
    )
    validateSession({ date: val })
  }

  const validateSession = async ({ date = null, time = null }) => {
    const newDate = date || startDate
    const newTime = time || startTime

    const { data } = await refetch({
      lessonId: booking.id,
      timespans: [
        {
          from: newDate.set({
            hour: newTime.hour,
            minute: newTime.minute,
          }),
          to: newDate
            .set({
              hour: newTime.hour,
              minute: newTime.minute,
            })
            .plus({ minutes: 60 }),
        },
      ],
    })

    const warningMessages = data?.lessonValidation.warnings.filter(
      message => message !== 'start_date_time must be before end_date_time',
    )
    setWarnings(warningMessages)
  }

  const timeLabel = formattedTimezone
    ? `Time (${formattedTimezone}) *`
    : 'Time *'

  const handleSave = async () => {
    setIsSaving(true)
    try {
      const { data } = await updateSession({
        variables: {
          id: booking.id,
          startDateTime: buildSessionStartDateTime().toISO(),
        },
      })

      if (data.updateSession.success) {
        onSuccess(data.updateSession.session)
        enqueueSnackbar(data.updateSession.message, { variant: 'success' })
        onBack()
      } else {
        enqueueSnackbar(data.updateSession.message, {
          variant: 'error',
        })
      }
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Box className={classes.editSessionRoot}>
      <LocalizationProvider dateAdapter={CustomLuxonAdapter} locale="en-us">
        <ThemeProvider theme={coachTheme}>
          <SubviewPageHeader pageTitle="Edit Session" onBack={onBack} />
          {warnings?.length > 0 ? (
            <Box
              display="flex"
              flexDirection="row"
              className={classes.editSessionWarning}
              alignItems="center"
            >
              <Box
                mt={1}
                display="flex"
                flexDirection="column"
                alignItems="start"
              >
                <Typography className={classes.editSessionWarningBody}>
                  This time may conflict with the following event(s)
                  <ul className={classes.editSessionWarningList}>
                    {warnings.map((warningMessage, index) => (
                      <li key={index}>
                        <Typography gutterBottom>
                          <strong>{warningMessage}</strong>
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Typography>
              </Box>
            </Box>
          ) : null}
          <Typography className={classes.editSessionHelper}>
            Select the date and time to reschedule this session.
          </Typography>
          <Grid container>
            <Grid item xs={12}>
              <Box mt={2} mb={2}>
                <DesktopDatePicker
                  id="start-date"
                  value={startDate}
                  format="MMM dd, yyyy"
                  minDate={currentDateTime}
                  onChange={handleDateChange}
                  label="Date *"
                  dayOfWeekFormatter={day => `${day}`}
                  closeOnSelect
                  sx={{ width: '100%' }}
                  slots={mergeDesktopDatePickerSlots({
                    openPickerIcon: DatePickerIcon,
                  })}
                  slotProps={mergeDesktopDatePickerSlotProps({
                    openPickerButton: {
                      sx: { color: PICKER_ICON_COLOR },
                    },
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      sx: outlinedPickerFieldSx,
                    },
                    popper: dialogPickerPopperProps,
                  })}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box mb={2}>
                <DesktopTimePicker
                  id="start-time"
                  label={timeLabel}
                  minutesStep={5}
                  closeOnSelect={false}
                  views={['hours']}
                  format={ENTER_TIME_FIELD_FORMAT}
                  viewRenderers={enterTimeViewRenderers}
                  value={startTime}
                  onChange={handleTimeChange}
                  onError={error => setInputError(error)}
                  sx={{ width: '100%' }}
                  slots={{
                    openPickerIcon: TimePickerIcon,
                  }}
                  slotProps={mergeEnterTimePickerSlotProps({
                    openPickerButton: {
                      sx: { color: PICKER_ICON_COLOR },
                    },
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      sx: outlinedPickerFieldSx,
                    },
                    popper: dialogPickerPopperProps,
                  })}
                />
              </Box>
            </Grid>
          </Grid>
          <Box className={classes.editSessionActions}>
            <Button color="primary" size="large" onClick={onBack}>
              Cancel
            </Button>
            <Button
              color="primary"
              variant="contained"
              size="large"
              disableElevation
              startIcon={<CheckIcon fontSize="small" />}
              disabled={!(startTime && startDate) || !!inputError || isSaving}
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>
        </ThemeProvider>
      </LocalizationProvider>
    </Box>
  )
}

export default EditSessionSubview
